import axios, { AxiosResponse } from 'axios';

import {
    NotificationHistory,
    UpdateNotificationStatuses,
    UpdateNotificationStatus,
    NotificationType,
    NotificationLink,
    DesktopAlert,
} from 'admin/notifications/Notifications.types';
import { CountResponse } from 'core/util/Response';
import { SearchRequest } from 'core/util/Search';

export const FieldName = {
    NotificationHistoryUserID: '__notification_history_userID__',
    NotificationHistoryChannel: '__notification_history_channel__',
    NotificationHistoryStatus: '__notification_history_status__',
    NotificationHistoryViewed: '__notification_history_viewed__',
    NotificationHistoryType: '__notification_history_type__',
    NotificationHistoryRemindedFlag: '__notification_history_reminded_flag__',
    NotificationHistoryID: '__notification_history_ID__',
    NotificationHistoryNotificationID: '__notification_id__',
    NotificationHistoryDeeplinkID: '__notification_history_deeplink_id__',
};

export const EventTypeValues = {
    NEW_REVIEW: NotificationType.Review,
    NOTIFICATION_REVIEW_RESPONSE_APPROVAL: NotificationType.ReviewResponseApproval,
    NEW_SOCIAL_POST: NotificationType.SocialPost,
    NEW_SOCIAL_POST_COMMENT: NotificationType.SocialReply,
    TICKET_CREATED_OR_UPDATED: NotificationType.Ticket,
    SOCIAL_POST_FAIL: NotificationType.ScheduledLocationPost,
    SOCIAL_LISTENING: NotificationType.Mention,
    SURVEY_RESULT: NotificationType.SurveyResult,
    SURVEY_RESPONSE: NotificationType.SurveyResponse,
    SURVEY_RESPONSE_APPROVAL: NotificationType.SurveyResponseApproval,
    SOCIAL_POST_APPROVAL: NotificationType.SocialPostApproval,
    // NEW_GOOGLE_QA: NotificationType.GoogleQA, // Deprecated: Google My Business Q&A API discontinued on November 3, 2025
    SUMMARY: NotificationType.Summary,
    SURVEY_LEGACY: NotificationType.Survey1,
    DATA_COLLECTION: NotificationType.DataCollectionStatus,
    INBOX_MESSAGE: NotificationType.InboxMessage,
    MESSAGE_RESPONSE_APPROVAL: NotificationType.MessageResponseApproval,
    CREDENTIAL: NotificationType.Credential,
    INTEGRATION: NotificationType.Integration,
    LISTINGS: NotificationType.Listings,
    DUPLICATE_PROFILES: NotificationType.DuplicateProfiles,
    PROFILE_DATA_ISSUES: NotificationType.ProfileErrorListings,
    PUBLISHER_SUGGESTIONS: NotificationType.PublisherSuggestions,
    LISTING_VERIFICATION: NotificationType.ListingVerification,
    PROFILE_CHANGES: NotificationType.ProfileChangeListings,
    CREDENTIAL_SUMMARY: NotificationType.CredentialSummary,
    TICKET_RESPONSE: NotificationType.TicketResponse,
    SOCIAL_NON_API_POST: NotificationType.SocialNonAPIPost,
    SOCIAL_POST_RESPONSE_APPROVAL: NotificationType.SocialPostReplyResponseApproval,
    INBOX_CONVERSATION_UPDATE: NotificationType.InboxConversationUpdate,
    ACTION_PLAN_CREATED_OR_UPDATED: NotificationType.ActionPlan,
    REVIEW3: NotificationType.Review3,
    REVIEW3_RESPONSE_APPROVAL: NotificationType.Review3ResponseApproval,
};

export const EventTypeOptions = [
    {
        label: 'ACTIONS',
        value: 'Ticket',
        options: [
            {
                value: EventTypeValues.TICKET_CREATED_OR_UPDATED,
                label: 'TICKET_CREATED_OR_UPDATED',
                defaultName: 'TICKET_CREATED_OR_UPDATED_ALERT',
            },
            {
                value: EventTypeValues.TICKET_RESPONSE,
                label: 'TICKET_RESPONSE',
                defaultName: 'TICKET_RESPONSE',
            },
        ],
    },
    {
        label: 'ACTION_PLANS',
        value: 'ActionPlans',
        options: [
            {
                value: EventTypeValues.ACTION_PLAN_CREATED_OR_UPDATED,
                label: 'ACTION_PLAN_CREATED_OR_UPDATED',
                defaultName: 'ACTION_PLAN_CREATED_OR_UPDATED_ALERT',
            },
        ],
    },
    {
        label: 'BUSINESS_LISTINGS',
        value: 'Listings',
        options: [
            // Deprecated: Google My Business Q&A API discontinued on November 3, 2025
            // {
            //     value: EventTypeValues.NEW_GOOGLE_QA,
            //     label: 'NEW_GOOGLE_QA',
            //     defaultName: 'NEW_GOOGLE_QA_ALERT',
            // },
            {
                value: EventTypeValues.LISTINGS,
                label: 'BL_TRAFFIC_METRICS',
                defaultName: 'BL_TRAFFIC_METRICS',
            },
            {
                value: EventTypeValues.PROFILE_CHANGES,
                label: 'PROFILE_CHANGES',
                defaultName: 'PROFILE_CHANGES',
            },
            {
                value: EventTypeValues.DUPLICATE_PROFILES,
                label: 'DUPLICATE_PROFILES',
                defaultName: 'DUPLICATE_PROFILES',
            },
            {
                value: EventTypeValues.PROFILE_DATA_ISSUES,
                label: 'PROFILE_DATA_ISSUES',
                defaultName: 'PROFILE_DATA_ISSUES',
            },
            {
                value: EventTypeValues.PUBLISHER_SUGGESTIONS,
                label: 'PUBLISHER_SUGGESTIONS',
                defaultName: 'PUBLISHER_SUGGESTIONS',
            },
            {
                value: EventTypeValues.LISTING_VERIFICATION,
                label: 'LISTING_VERIFICATION',
                defaultName: 'LISTING_VERIFICATION',
            },
        ],
    },
    {
        label: 'CREDENTIALS',
        value: 'Credentials',
        options: [
            {
                value: EventTypeValues.CREDENTIAL,
                label: 'NOTIFICATION_CREDENTIAL_NOT_CONNECTED',
                defaultName: 'NOTIFICATION_CREDENTIAL_NOT_CONNECTED_ALERT',
            },
            {
                value: EventTypeValues.CREDENTIAL_SUMMARY,
                label: 'NOTIFICATION_CREDENTIAL_SUMMARY',
                defaultName: 'NOTIFICATION_CREDENTIAL_SUMMARY',
            },
        ],
    },
    {
        label: 'INBOX',
        value: 'Inbox',
        options: [
            {
                value: EventTypeValues.INBOX_MESSAGE,
                label: 'INBOX_MESSAGE',
                defaultName: 'INBOX_MESSAGE_ALERT',
            },
            {
                value: EventTypeValues.MESSAGE_RESPONSE_APPROVAL,
                label: 'MESSAGE_RESPONSE_APPROVAL',
                defaultName: 'MESSAGE_RESPONSE_APPROVAL',
            },
            {
                value: EventTypeValues.INBOX_CONVERSATION_UPDATE,
                label: 'INBOX_CONVERSATION_UPDATE',
                defaultName: 'INBOX_CONVERSATION_UPDATE',
            },
        ],
    },
    {
        label: 'INTEGRATIONS',
        value: 'Integrations',
        options: [
            {
                value: EventTypeValues.INTEGRATION,
                label: 'INTEGRATION_STATUS',
                defaultName: 'INTEGRATION_STATUS_ALERT',
            },
        ],
    },
    {
        label: 'REVIEW',
        value: 'Review',
        options: [
            {
                value: EventTypeValues.NEW_REVIEW,
                label: 'NEW_REVIEW',
                defaultName: 'NEW_REVIEW_ALERT',
            },
            {
                value: EventTypeValues.NOTIFICATION_REVIEW_RESPONSE_APPROVAL,
                label: 'NOTIFICATION_REVIEW_RESPONSE_APPROVAL',
                defaultName: 'NOTIFICATION_REVIEW_RESPONSE_APPROVAL',
            },
            {
                value: EventTypeValues.SURVEY_LEGACY,
                label: 'SURVEY_LEGACY',
                defaultName: 'SURVEY_LEGACY_ALERT',
            },
            {
                value: EventTypeValues.DATA_COLLECTION,
                label: 'DATA_COLLECTION',
                defaultName: 'DATA_COLLECTION_ALERT',
            },
        ],
    },
    {
        label: 'REVIEW',
        value: 'Review3',
        options: [
            {
                value: EventTypeValues.REVIEW3,
                label: 'NEW_REVIEW',
                defaultName: 'NEW_REVIEW_ALERT',
            },
            {
                value: EventTypeValues.REVIEW3_RESPONSE_APPROVAL,
                label: 'NOTIFICATION_REVIEW_RESPONSE_APPROVAL',
                defaultName: 'NOTIFICATION_REVIEW_RESPONSE_APPROVAL',
            },
        ],
    },
    {
        label: 'SOCIAL',
        value: 'Social',
        options: [
            {
                value: EventTypeValues.NEW_SOCIAL_POST,
                label: 'NEW_SOCIAL_POST',
                defaultName: 'NEW_SOCIAL_POST_ALERT',
            },
            {
                value: EventTypeValues.NEW_SOCIAL_POST_COMMENT,
                label: 'NEW_SOCIAL_POST_COMMENT',
                defaultName: 'NEW_SOCIAL_POST_COMMENT_ALERT',
            },
            {
                value: EventTypeValues.SOCIAL_POST_FAIL,
                label: 'SOCIAL_POST_FAIL',
                defaultName: 'SOCIAL_POST_FAIL_ALERT',
            },
            {
                value: EventTypeValues.SOCIAL_POST_APPROVAL,
                label: 'SOCIAL_POST_APPROVAL',
                defaultName: 'SOCIAL_POST_APPROVAL',
            },
            {
                value: EventTypeValues.SOCIAL_POST_RESPONSE_APPROVAL,
                label: 'SOCIAL_POST_RESPONSE_APPROVAL',
                defaultName: 'SOCIAL_POST_RESPONSE_APPROVAL',
            },
        ],
    },
    {
        label: 'SUMMARY',
        value: 'Summary',
        options: [
            {
                value: EventTypeValues.SUMMARY,
                label: 'NEW_SUMMARY',
                defaultName: 'NEW_SUMMARY',
            },
        ],
    },
    {
        label: 'SURVEYS',
        value: 'Surveys',
        options: [
            {
                value: EventTypeValues.SURVEY_RESULT,
                label: 'SURVEY_RESULT',
                defaultName: 'SURVEY_RESULT_ALERT',
            },
            {
                value: EventTypeValues.SURVEY_RESPONSE_APPROVAL,
                label: 'SURVEY_RESPONSE_APPROVAL',
                defaultName: 'SURVEY_RESPONSE_APPROVAL',
            },
            {
                value: EventTypeValues.SURVEY_RESPONSE,
                label: 'SURVEY_RESPONSE',
                defaultName: 'SURVEY_RESPONSE_ALERT',
            },
        ],
    },
];

export const LegacyReviewsTypes = [
    EventTypeValues.NEW_REVIEW,
    EventTypeValues.SURVEY_LEGACY,
    EventTypeValues.DATA_COLLECTION,
    EventTypeValues.NOTIFICATION_REVIEW_RESPONSE_APPROVAL,
];

export const Reviews3Types = [EventTypeValues.REVIEW3, EventTypeValues.REVIEW3_RESPONSE_APPROVAL];

export enum SortOrder {
    Asc = 1,
    Desc = 2,
}

export const getNotificationsHistories = async (
    tenantId?: number,
    args?: SearchRequest,
): Promise<NotificationHistory[]> => {
    const params: any = { tenantId, args };

    const response: AxiosResponse = await axios.get(`/api/tenants/${tenantId}/notification-histories`, {
        params,
    });

    return response.data;
};

export const updateNotificationHistoryStatus = async (
    tenantId?: number,
    args?: UpdateNotificationStatus,
): Promise<NotificationHistory> => {
    const { data } = await axios.post<NotificationHistory>(
        `/api/tenants/${tenantId}/notification-histories/update-status`,
        { args },
    );

    return data;
};

export const updateRemindMeLater = async (
    tenantId?: number,
    notificationHistoryIds?: string[],
): Promise<NotificationHistory> => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/notification-histories/update-remind-me-later-status`, {
        args: {
            notificationHistoryID: notificationHistoryIds,
        },
    });

    return data;
};

export const updateNotificationHistoryStatuses = async (
    tenantId?: number,
    args?: UpdateNotificationStatuses,
): Promise<NotificationHistory> => {
    const { data } = await axios.post<NotificationHistory>(
        `/api/tenants/${tenantId}/notification-histories/update-statuses`,
        { args },
    );

    return data;
};

export const countNotificationsHistories = async (
    tenantId?: number,
    search?: SearchRequest,
): Promise<CountResponse> => {
    const { data }: AxiosResponse = await axios.get(`/api/tenants/${tenantId}/notification-histories/count`, {
        params: {
            search,
        },
    });

    return data;
};

export const markAllNotificationHistoriesAsViewed = async (
    tenantId?: number,
    userId?: number,
): Promise<NotificationHistory> => {
    const { data } = await axios.post<NotificationHistory>(`/api/tenants/${tenantId}/notification-histories/viewed`, {
        userId,
    });

    return data;
};

export interface GetNotificationLinkArgs {
    notificationObjectId: string;
    notificationType: NotificationType;
    eventType: string;
}

export const getNotificationLink = async (
    tenantId?: number,
    notificationId?: string,
    args?: GetNotificationLinkArgs,
): Promise<NotificationLink> => {
    const params: any = { notificationId, args };
    const { data }: AxiosResponse = await axios.get(`/api/tenants/${tenantId}/notifications/${notificationId}/link`, {
        params,
    });

    return data;
};

export interface GetNotificationsArgs {
    RoleId?: string;
    UserId?: number;
    Hide: boolean;
}

export const getNotifications = async (tenantId?: number, args?: GetNotificationsArgs): Promise<NotificationLink> => {
    const params: any = { args };
    const { data }: AxiosResponse = await axios.get(`/api/tenants/${tenantId}/notifications`, {
        params,
    });

    return data;
};

export const sendNotification = async (
    tenantId?: number,
    notificationId?: string,
    // args?: any,
): Promise<NotificationLink> => {
    // const params: any = { args };
    const { data }: AxiosResponse = await axios.put(`/api/tenants/${tenantId}/notifications/${notificationId}/send`);

    return data;
};

export const activateNotification = async (
    tenantId: number,
    notificationId: string,
    args: any,
): Promise<NotificationLink> => {
    const params: any = { tenantId, notificationId, args };
    const { data }: AxiosResponse = await axios.put(
        `/api/tenants/${tenantId}/notifications/${notificationId}/activate`,
        {
            ...params,
        },
    );

    return data;
};

export const removeNotification = async (
    tenantId?: number,
    notificationId?: string,
    args?: any,
): Promise<NotificationLink> => {
    const params: any = { args };
    const { data }: AxiosResponse = await axios.delete(`/api/tenants/${tenantId}/notifications/${notificationId}`, {
        params,
    });

    return data;
};

export const removeNotificationHistory = async (
    tenantId?: number,
    notificationId?: string,
    args?: any,
): Promise<string> => {
    const params: any = { args };
    const { data }: AxiosResponse = await axios.delete(
        `/api/tenants/${tenantId}/notification-histories/${notificationId}`,
        {
            params,
        },
    );

    return data;
};

export const getDesktopAggregatedAlerts = async (
    tenantId?: number,
    args?: { eventTypes: string[] },
): Promise<DesktopAlert[]> => {
    const { data }: AxiosResponse = await axios.post(`/api/tenants/${tenantId}/desktop-notifications`, args);

    return data;
};
